import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Spinner, ListBullets, Files, Clock, ShieldWarning, UsersThree, Microphone, Flag, Question, TreeStructure, ShieldCheck, Gauge, Steps } from '@phosphor-icons/react';
import { useCaseData } from './case-detail/hooks/useCaseData';
import { useCaseActions } from './case-detail/hooks/useCaseActions';
import { CaseHeader } from './case-detail/components/CaseHeader';
import { CaseStats } from './case-detail/components/CaseStats';
import { ShareModal } from './case-detail/components/ShareModal';
import { CaseChatPanel } from './case-detail/components/CaseChatPanel';
import { VersionBar } from './case-detail/components/VersionBar';
import { AnalysisProgressOverlay } from './case-detail/components/AnalysisProgressOverlay';
import { OverviewTab } from './case-detail/tabs/OverviewTab';
import { DocumentsTab } from './case-detail/tabs/DocumentsTab';
import { TimelineTab } from './case-detail/tabs/TimelineTab';
import { ContradictionsTab } from './case-detail/tabs/ContradictionsTab';
import { CollaboratorsTab } from './case-detail/tabs/CollaboratorsTab';
import { NarrationTab } from './case-detail/tabs/NarrationTab';
import { ClaimsTab } from './case-detail/tabs/ClaimsTab';
import { OpenQuestionsTab } from './case-detail/tabs/OpenQuestionsTab';
import { IssuesTab } from './case-detail/tabs/IssuesTab';
import { PrivacyTab } from './case-detail/tabs/PrivacyTab';
import { ScorecardTab } from './case-detail/tabs/ScorecardTab';
import { EscalationTab } from './case-detail/tabs/EscalationTab';

const TABS = [
  { id: 'overview', label: 'Overview', icon: ListBullets },
  { id: 'scorecard', label: 'Scorecard', icon: Gauge },
  { id: 'documents', label: 'Documents', icon: Files },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'issues', label: 'Issues', icon: TreeStructure },
  { id: 'claims', label: 'Claims', icon: Flag },
  { id: 'narration', label: 'Narration', icon: Microphone },
  { id: 'contradictions', label: 'Contradictions', icon: ShieldWarning },
  { id: 'open-questions', label: 'Open Questions', icon: Question },
  { id: 'escalation', label: 'Escalation', icon: Steps },
  { id: 'privacy', label: 'Privacy', icon: ShieldCheck },
  { id: 'collaborators', label: 'Team', icon: UsersThree },
];

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { caseData, documents, timeline, contradictions, chatMessages, setChatMessages, aiUsage, analysisProgress, setAnalysisProgress, loading, loadAll } = useCaseData(caseId);
  const actions = useCaseActions(caseId, loadAll, setAnalysisProgress);
  const [activeTab, setActiveTab] = useState('overview');
  const [chatOpen, setChatOpen] = useState(false);
  const [shareModal, setShareModal] = useState(false);

  if (loading) return <AppLayout><div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-[#002FA7]" /></div></AppLayout>;
  if (!caseData) return <AppLayout><div className="p-8 text-center text-[#6B7280] font-ibm">Case not found</div></AppLayout>;

  const analyzedDocs = documents.filter(d => d.analysis_status === 'analyzed');
  const risk = caseData.risk_indicators || {};

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-6 sm:p-8" data-testid="case-detail-page">
        <CaseHeader
          caseData={caseData} aiUsage={aiUsage}
          exporting={actions.exporting} exportFile={actions.exportFile}
          onShare={() => setShareModal(true)} onOpenChat={() => setChatOpen(true)}
          onBack={() => navigate('/dashboard')}
        />

        <CaseStats documents={documents} timeline={timeline} contradictions={contradictions} analyzedDocs={analyzedDocs} />

        <VersionBar caseId={caseId} initialVersions={caseData.versions} initialActive={caseData.active_version} onChanged={() => { loadAll(); setActiveTab('overview'); }} />

        {/* Tabs */}
        <div className="flex flex-wrap border-b border-[#E5E7EB] mb-6">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-ibm border-b-2 transition-all -mb-px ${activeTab === t.id ? 'border-[#002FA7] text-[#002FA7] font-semibold' : 'border-transparent text-[#6B7280] hover:text-[#09090B]'}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab caseData={caseData} risk={risk} generateSummary={actions.generateSummary} generatingSummary={actions.generatingSummary} caseId={caseId} loadAll={loadAll} />}
        {activeTab === 'scorecard' && <ScorecardTab caseId={caseId} />}
        {activeTab === 'documents' && <DocumentsTab documents={documents} uploading={actions.uploading} bulkUploading={actions.bulkUploading} analyzing={actions.analyzing} batchAnalyzing={actions.batchAnalyzing} generatingNotes={actions.generatingNotes} fileInputRef={actions.fileInputRef} bulkInputRef={actions.bulkInputRef} uploadFile={actions.uploadFile} bulkUpload={actions.bulkUpload} analyzeDoc={actions.analyzeDoc} batchAnalyze={actions.batchAnalyze} generateNotes={actions.generateNotes} loadAll={loadAll} />}
        {activeTab === 'timeline' && <TimelineTab timeline={timeline} xlsxInputRef={actions.xlsxInputRef} xlsxUploading={actions.xlsxUploading} uploadXlsx={actions.uploadXlsx} caseId={caseId} loadAll={loadAll} />}
        {activeTab === 'issues' && <IssuesTab caseId={caseId} />}
        {activeTab === 'claims' && <ClaimsTab caseId={caseId} />}
        {activeTab === 'narration' && <NarrationTab caseId={caseId} />}
        {activeTab === 'contradictions' && <ContradictionsTab contradictions={contradictions} runContradictions={actions.runContradictions} detectingContras={actions.detectingContras} analyzedDocs={analyzedDocs} loadAll={loadAll} />}
        {activeTab === 'open-questions' && <OpenQuestionsTab caseId={caseId} />}
        {activeTab === 'escalation' && <EscalationTab caseId={caseId} />}
        {activeTab === 'privacy' && <PrivacyTab caseId={caseId} />}
        {activeTab === 'collaborators' && <CollaboratorsTab caseId={caseId} />}

        {shareModal && <ShareModal caseId={caseId} onClose={() => { setShareModal(false); }} />}
        {chatOpen && <CaseChatPanel caseId={caseId} messages={chatMessages} setMessages={setChatMessages} onClose={() => setChatOpen(false)} />}
      </div>
      <AnalysisProgressOverlay progress={analysisProgress} />
    </AppLayout>
  );
}
